/**
 * Exception Config Registry
 *
 * Maps every exception code to a shared `ExceptionConfig` consumed by
 * both Inspector (`DynamicExceptionRoute`) and Platform Reports. One
 * config serves both variants; column visibility is filtered by
 * `contexts: ['inspector' | 'platform']`.
 *
 * ─── Adding a new exception type — full checklist ───────────────────
 *
 * Backend:
 *   1. Alembic migration → INSERT into `exception_types` with code,
 *      data_domain, function_name, markets, thresholds, visible_to_roles.
 *   2. Tagger fn under
 *      `backend/shared/exception_taggers/registry/{domain}.py`,
 *      registered in that file's `register_functions()`.
 *   3. Route the code in
 *      `backend/shared/exception_queries/inventory.py:_CODE_HANDLERS`
 *      ("formatter" | "inline" | "po") so `run_single` knows which row
 *      builder to use.
 *
 * Frontend:
 *   4. For product-backed codes, add a 5-line config using
 *      `buildProductExceptionConfig(...)` from
 *      `shared/productExceptionColumns.ts`. For other domains, copy a
 *      simple existing config (e.g. `product-setup/noCategoryExceptions.ts`).
 *   5. Import it here and push onto `ALL_CONFIGS`.
 *
 * Skip any step and the Dashboard renders "No component found for
 * exception type …".
 * ────────────────────────────────────────────────────────────────────
 */
import { simpleSalesConfig } from './sales/simpleExceptions'
import { duplicateSaleConfig } from './sales/duplicateSaleExceptions'
import { compareSalesConfig } from './sales/compareExceptions'
import { surchargeConfig } from './sales/surchargeExceptions'
import { noCategoryConfig } from './product-setup/noCategoryExceptions'
import { variantGroupDormantConfig } from './product-setup/variantGroupExceptions'
import { supplierVariantSoldConfig } from './product-setup/supplierVariantSoldExceptions'
import { duplicateConfig } from './product-setup/duplicateExceptions'
import { missingPriceConfig } from './product-pricing/missingPriceExceptions'
import { supplierOverRetailConfig } from './product-pricing/supplierOverRetailExceptions'
import { avgCostOverRetailConfig } from './product-pricing/avgCostOverRetailExceptions'
import { avgCostZeroConfig } from './product-pricing/avgCostZeroExceptions'
import { noninvHasAvgCostConfig } from './product-pricing/noninvHasAvgCostExceptions'
import { overduePoConfig } from './stock-management/overduePoExceptions'
import { pendingTransfersConfig } from './stock-management/pendingTransfersExceptions'
import { inactiveWithStockConfig } from './stock-management/inactiveWithStockExceptions'
import {
  negativeStockBusinessConfig,
  negativeStockOutletConfig,
} from './stock-management/negativeStockExceptions'
import {
  registerLowClosureRateConfig,
  registerOpenTooLongConfig,
  registerPrefixIncorrectConfig,
} from './system/registerExceptions'
import { outletConfig } from './system/outletExceptions'
import {
  helperProductsPresenceConfig,
  dosageNamePresenceConfig,
} from './product-setup/masterPresenceExceptions'
import { categoryAlignmentConfig } from './product-setup/categoryAlignmentExceptions'
import { configFlagConfig } from './system/configFlagExceptions'
import { unapprovedStaffEmailConfig } from './system/unapprovedStaffEmailExceptions'
import { skimmerSyncInvoiceConfig } from './sales/skimmerSyncInvoiceExceptions'
import { skimmerSyncCustomerConfig } from './client/skimmerSyncCustomerExceptions'
import { monthlyCogsConfig } from './accounting/monthlyCogsExceptions'
import { xeroCoaConfig } from './accounting/xeroCoaExceptions'
import { xeroAccountConfig } from './accounting/productXeroAccountExceptions'
import { unsentPoConfig } from './accounting/unsentPoExceptions'
import { registerClosureMissingInXeroConfig } from './accounting/registerXeroClosureExceptions'
import { duplicateClosureBillConfig } from './accounting/duplicateClosureBillExceptions'
import { manualJournalOnCogsBaseConfig } from './accounting/manualJournalOnCogsBase'
import type { ExceptionConfig, ExceptionColumnDef, ExceptionContext } from './types'
import type { ExceptionObjectType } from '../../utils/urls'

/**
 * Configs grouped by inspector object_type (the URL segment under
 * `/inspect/{fp}/exceptions/`). Keeps folder structure and route structure
 * aligned: adding a config means adding it to its object_type bucket here,
 * and `getExceptionObjectType()` falls out for free.
 */
const CONFIGS_BY_OBJECT_TYPE: Record<ExceptionObjectType, ExceptionConfig[]> = {
  sales: [
    simpleSalesConfig,
    compareSalesConfig,
    surchargeConfig,
    skimmerSyncInvoiceConfig,
    // Grouped one-row-per-group view (fetchOverride to the duplicate-sales
    // tools endpoint) - owns ex-sale-ls-duplicate on the exception route.
    duplicateSaleConfig,
  ],
  'product-setup': [
    noCategoryConfig,
    variantGroupDormantConfig,
    supplierVariantSoldConfig,
    duplicateConfig,
    helperProductsPresenceConfig,
    dosageNamePresenceConfig,
    categoryAlignmentConfig,
  ],
  'product-pricing': [
    missingPriceConfig,
    supplierOverRetailConfig,
    avgCostOverRetailConfig,
    avgCostZeroConfig,
    noninvHasAvgCostConfig,
  ],
  'stock-management': [
    overduePoConfig,
    pendingTransfersConfig,
    inactiveWithStockConfig,
    negativeStockBusinessConfig,
    negativeStockOutletConfig,
  ],
  systems: [
    registerLowClosureRateConfig,
    registerOpenTooLongConfig,
    registerPrefixIncorrectConfig,
    outletConfig,
    configFlagConfig,
    unapprovedStaffEmailConfig,
  ],
  client: [
    skimmerSyncCustomerConfig,
  ],
  accounting: [
    monthlyCogsConfig,
    xeroCoaConfig,
    xeroAccountConfig,
    unsentPoConfig,
    registerClosureMissingInXeroConfig,
    duplicateClosureBillConfig,
    manualJournalOnCogsBaseConfig,
  ],
}

const ALL_CONFIGS: ExceptionConfig[] = Object.values(CONFIGS_BY_OBJECT_TYPE).flat()

/**
 * Code-to-config lookup map (built once at import time)
 */
const codeMap = new Map<string, ExceptionConfig>()
const codeToObjectType = new Map<string, ExceptionObjectType>()
for (const [objectType, configs] of Object.entries(CONFIGS_BY_OBJECT_TYPE) as [
  ExceptionObjectType,
  ExceptionConfig[],
][]) {
  for (const config of configs) {
    for (const code of config.codes) {
      codeMap.set(code, config)
      codeToObjectType.set(code, objectType)
    }
  }
}

/**
 * Get the shared exception config for a given exception code.
 * Returns null if the code is not registered (fall back to legacy).
 */
export function getExceptionConfig(code: string): ExceptionConfig | null {
  return codeMap.get(code) ?? null
}

/**
 * Get the config a platform report should render for a given code.
 * After the product-exception unification, inspector and platform share
 * the same configs — this remains a separate export for any future
 * per-variant divergence.
 */
export function getPlatformExceptionConfig(code: string): ExceptionConfig | null {
  return codeMap.get(code) ?? null
}

/**
 * Every code the platform side can render.
 */
export function getAllPlatformRegisteredCodes(): string[] {
  return Array.from(codeMap.keys())
}

/**
 * Get columns filtered for a specific context (inspector or platform).
 */
export function getColumnsForContext(
  config: ExceptionConfig,
  context: ExceptionContext
): ExceptionColumnDef[] {
  return config.columns.filter(col => {
    if (!col.contexts) return true
    return col.contexts.includes(context)
  })
}

/**
 * Get all registered exception codes.
 */
export function getAllRegisteredCodes(): string[] {
  return Array.from(codeMap.keys())
}

/**
 * Resolve the inspector object_type (URL segment) for an exception code,
 * e.g. `ex-sale-ls-duplicate` -> `sales`. Returns null for codes that
 * aren't registered. Used by Platform Reports to deep-link FP rows
 * straight to the per-FP exception view instead of the inspector landing.
 */
export function getExceptionObjectType(code: string): ExceptionObjectType | null {
  return codeToObjectType.get(code) ?? null
}
